import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition, SlotGroupResult } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
  usePage,
  useEditMode,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import pagesData from '../pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { handleScheduleTourClick, scheduleTourHref } from '../utils/scheduleTour';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

/** Page aliases that should never appear in the main navigation.
 *
 * These are utility / legal / funnel pages that stay reachable via direct
 * links or the footer but shouldn't clutter the top-level nav. Home is
 * excluded even when `visible: true` — the logo already links to `/`.
 * Content pages like `virtual-tour` and `pet-policy` ARE surfaced in the
 * nav (Virtual Tour is additionally gated on the property actually
 * having a virtual tour — see `buildNavLinks` / `hasVirtualTours` below). */
const NAV_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

/** Normalize a path for comparison: drop the query/hash and any trailing
    slash so "/floor-plans/", "/floor-plans?x=1" and "/floor-plans" all match,
    while keeping the root "/" intact. */
function normalizePath(p?: string | null): string {
  if (!p) return '/';
  const path = p.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path || '/';
}

/** A nav link is "active" when its href matches the current page — or, for a
    parent link with a dropdown, when any of its children match (so the parent
    stays highlighted while you're on one of its sub-pages). */
function isNavLinkActive(link: NavLink, currentPath: string): boolean {
  const current = normalizePath(currentPath);
  if (normalizePath(link.href) === current) return true;
  return !!link.children?.some((c) => normalizePath(c.href) === current);
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 * Orphaned children (parent missing or hidden) fall back to the top level
 * so a page is never silently dropped from the nav.
 *
 * `hasInteractiveLeasing` controls the dynamic visibility of the
 * Interactive Map page — that page renders the ApartmentSync (Repli360 /
 * RRAC) "interactive leasing view" widget, which is keyed by two per-
 * property custom fields: `custom_apartmentsync_javascript_id` (the
 * encoded token in the script URL) and `custom_apartmentsync_config_id`
 * (the site identifier). Both are declared as required-style tokens on
 * the apartment-sync-map block, and a property without both is a
 * half-configured / no-real-map state (Centre Point being the canonical
 * example — it has neither, but earlier versions of this check let it
 * leak into the nav when only one ID was populated as a placeholder).
 * If either is empty we drop the page from the nav (the SSG also marks
 * the page hidden + noindex so it isn't crawled — see ssg-entry.ts).
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  // First pass: create a NavLink for every eligible page.
  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  // Second pass: attach children to parents, preserving sort order. Pages
  // whose parent isn't eligible (hidden / excluded / missing) bubble up to
  // the top level so we never lose a page from the nav.
  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    // Header bar is solid light / white — default to mono black so a white
    // or light primary logo never disappears into the background. Editors
    // can still pick the color primary via suggestedFields when it has
    // enough contrast.
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_black_logo', suggestedFields: ['property_mono_black_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Portal' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Primary header CTA. Labeled "Schedule a Tour" (rendered as a solid, filled
// button — see `.nav-cta-btn` below). The slot path stays
// `header.schedule_tour_link` so existing saved content / bindings keep
// resolving; only the displayed label + default destination changed.
const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule a Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Address row inside the mobile drawer. Promoted from a bare
// `header.full_address` content token + auto-generated Google Maps
// directions URL to a slot group so editors can pick the address text,
// the destination URL, and the link target independently — rather than
// having the URL forced to the property's `google_place_url` (or a
// synthesized `/maps/dir/?destination=...` fallback) with no override.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place / business / full
    // address / lat-lon fallbacks). An explicit editor override wins.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ---------------------------------------------------------------------------
// Office hours helpers (mirrored from Footer for the mobile drawer contact
// block). Groups consecutive days that share the same open/close window so
// the drawer can render "Mon - Fri: 9:00 am - 5:00 pm" style lines.
// ---------------------------------------------------------------------------

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasHoursValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function formatOfficeTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  label: string;
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasHoursValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (!byDay.has(key)) byDay.set(key, entry);
  }

  const extraKeys = [...byDay.keys()].filter((k) => !DAY_ORDER.includes(k));
  const allKeys = [...DAY_ORDER, ...extraKeys];
  const groups: GroupedHours[] = [];

  for (const key of allKeys) {
    const entry = byDay.get(key);
    const dayLabel = DAY_LABELS[key] || entry?.day || key;
    const dayShortLabel = DAY_SHORT_LABELS[key] || (entry?.day || key).slice(0, 3);
    const isClosed = !entry || (!hasHoursValue(entry.open_time) && !hasHoursValue(entry.close_time));
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatOfficeTime(entry.open_time || '')} - ${formatOfficeTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayLabel, shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

/** Drawer hours line: ranges use short labels ("Mon - Fri"), single days use
    the full name ("Saturday") to match the Belmont mobile menu reference. */
function formatDrawerHoursLabel(group: GroupedHours): string {
  return group.shortLabel.includes(' - ') ? group.shortLabel : group.label;
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

// ============================================================================
// Chevron Down Icon for "More" dropdown
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// ============================================================================
// Desktop Nav Link
// ============================================================================

function DesktopNavLink({ link, currentPath }: { link: NavLink; currentPath: string }) {
  const hasChildren = !!(link.children && link.children.length);
  const active = isNavLinkActive(link, currentPath);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click-outside / Escape close — mirrors the "More" dropdown so the menu
  // dismisses cleanly when focus or pointer leaves the parent group.
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        aria-current={active ? 'page' : undefined}
        className={`rise-nav-darken-hover font-body text-[13px] font-normal tracking-wider text-[var(--vibe-color-text)] transition-colors py-2 whitespace-nowrap${active ? ' nav-link-active' : ''}`}
      >
        {link.label}
      </a>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* The parent label is still a real link so clicking it navigates to
          the parent page; the chevron + dropdown surface the child pages on
          hover/focus without stealing the primary affordance. */}
      <a
        href={link.href}
        aria-current={active ? 'page' : undefined}
        className={`rise-nav-darken-hover font-body text-[13px] font-normal tracking-wider text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap${active ? ' nav-link-active' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        onFocus={() => setOpen(true)}
      >
        {link.label}
        <ChevronDownIcon
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </a>

      <div
        className={`nav-more-dropdown absolute left-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1'
        }`}
        style={{ backgroundColor: 'var(--vibe-color-background)' }}
      >
        {link.children!.map((child) => {
          const childActive = normalizePath(child.href) === normalizePath(currentPath);
          return (
            <a
              key={child.href}
              href={child.href}
              aria-current={childActive ? 'page' : undefined}
              className={`rise-nav-darken-hover block px-5 py-3 font-body text-[13px] font-normal tracking-wider text-[var(--vibe-color-text)] transition-colors${childActive ? ' nav-link-active' : ''}`}
            >
              {child.label}
            </a>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================

function DesktopNav({ navLinks, currentPath }: { navLinks: NavLink[]; currentPath: string }) {
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    const computedGap = parseFloat(getComputedStyle(container).gap) || 20;
    const moreButtonWidth = 90;

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      const needed = totalWidth + widthWithGap + (i < children.length - 1 ? moreButtonWidth + computedGap : 0);

      if (needed > availableWidth && i < children.length) {
        const remainingWidth = totalWidth + widthWithGap;
        let allFit = true;
        let testWidth = remainingWidth;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        if (allFit) {
          fitCount = children.length;
        } else {
          fitCount = i;
        }
        break;
      }
      totalWidth += widthWithGap;
      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();
    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  return (
    <>
      {/* Hidden measurement row. Parent links with children render with a
          chevron in the live nav, so we mirror that here so the per-link
          offsetWidth used by the overflow calculation matches the visible
          width — otherwise the chevron would push the rightmost link out
          unexpectedly when the row is near full. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="flex items-center gap-5 absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link, i) => (
          <span
            key={link.href}
            className="font-body text-[13px] font-normal tracking-wider inline-flex items-center"
          >
            {/* Mirror the visible separator so each measured item includes the
                dot + its margin — keeps the overflow math accurate. */}
            {i > 0 ? <span className="nav-sep" aria-hidden="true">·</span> : null}
            <span className="inline-flex items-center gap-1">
              {link.label}
              {link.children && link.children.length ? (
                <ChevronDownIcon className="w-3.5 h-3.5" />
              ) : null}
            </span>
          </span>
        ))}
      </div>

      {/* Visible navigation */}
      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-end gap-2 xl:gap-3 flex-1 min-w-0 mx-6"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link, i) => (
          <span
            key={link.href}
            className="inline-flex items-center font-body text-[13px] font-normal tracking-wider"
          >
            {/* Dot separator between page names (matches the demo). Sits
                between the links — never before the first one — and is a
                sibling of the link so it stays out of the active-link bar. */}
            {i > 0 ? <span className="nav-sep" aria-hidden="true">·</span> : null}
            <DesktopNavLink link={link} currentPath={currentPath} />
          </span>
        ))}

        {overflowLinks.length > 0 && (
          <div
            ref={moreRef}
            className="relative"
            // Mirror the DesktopNavLink hover behavior — the "More"
            // overflow dropdown opens on hover/focus just like the
            // "Floor Plans" parent dropdown so the two affordances feel
            // identical. The click handler stays as a fallback for
            // touch devices where hover doesn't fire, and the
            // click-outside listener still dismisses the menu when the
            // pointer leaves the group entirely.
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
          >
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              onFocus={() => setMoreOpen(true)}
              className="rise-nav-darken-hover font-body text-[13px] font-normal tracking-wider text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>

            <div
              className={`nav-more-dropdown absolute right-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
                moreOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-1'
              }`}
              style={{ backgroundColor: 'var(--vibe-color-background)' }}
            >
              {overflowLinks.map((link) => (
                <React.Fragment key={link.href}>
                  <a
                    href={link.href}
                    aria-current={normalizePath(link.href) === normalizePath(currentPath) ? 'page' : undefined}
                    className={`rise-nav-darken-hover block px-5 py-3 font-body text-[13px] font-normal tracking-wider text-[var(--vibe-color-text)] transition-colors${normalizePath(link.href) === normalizePath(currentPath) ? ' nav-link-active' : ''}`}
                  >
                    {link.label}
                  </a>
                  {link.children?.map((child) => (
                    <a
                      key={child.href}
                      href={child.href}
                      aria-current={normalizePath(child.href) === normalizePath(currentPath) ? 'page' : undefined}
                      // Indent child rows so the More dropdown reads as a
                      // nested list when a parent (e.g. Floor Plans) gets
                      // pushed into overflow and we want its sub-items to
                      // remain reachable from the dropdown.
                      className={`rise-nav-darken-hover block pl-9 pr-5 py-3 font-body text-[13px] font-normal tracking-wider text-[var(--vibe-color-text-muted)] transition-colors${normalizePath(child.href) === normalizePath(currentPath) ? ' nav-link-active' : ''}`}
                    >
                      {child.label}
                    </a>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
  currentPath,
}: {
  link: NavLink;
  onNavigate: () => void;
  currentPath: string;
  /** Retained in callers for parity with desktop but intentionally
      unused — see comment below. Children render flush with their
      parents in the drawer because the visual offset reads as misaligned
      noise in a vertical list (the desktop hover dropdown already
      conveys the hierarchy there). */
  isChild?: boolean;
}) {
  const active = normalizePath(link.href) === normalizePath(currentPath);
  return (
    <a
      href={link.href}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={`mobile-nav-link rise-nav-darken-hover block py-4 px-6 font-body text-base sm:text-lg font-normal text-[var(--vibe-color-primary)] transition-colors border-b border-[var(--vibe-color-border)]${active ? ' nav-link-active' : ''}`}
    >
      {link.label}
    </a>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: Single navigation bar (logo | nav links | utility + CTA).
// The old 3-bar stack (banner + utility bar + nav bar) is replaced by one
// unified bar. Utility links (phone, Apply Now, Resident Access) are
// inlined into the right side of the nav bar on desktop, and folded into
// the mobile drawer on small screens.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);

  // The header is a solid, opaque white bar that does NOT overlay the hero
  // (matching the Belmont demo) — nav text / logo / CTA all render against the
  // white background, so there's no transparent-over-hero state, scroll-driven
  // color transition, or luminance-based contrast picking to manage anymore.
  // A spacer below reserves the header's height in the document flow so the
  // hero (and every page's content) starts beneath the bar.

  // Current page path — drives the active-link "double bar" indicator. We
  // read it from the runtime page context (available at SSG time AND on the
  // client) so the active state is baked into the static HTML and matches on
  // hydration; the window.location fallback only kicks in for preview/dev
  // paths that aren't wired through usePage().
  const pageCtx = usePage();
  const currentPath =
    pageCtx?.path ?? (typeof window !== 'undefined' ? window.location.pathname : '/');

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav when the property has no
  // ApartmentSync (Repli360 / RRAC) IDs to drive the "interactive
  // leasing view" widget on that page. We require BOTH the JavaScript
  // ID (script URL token) and the Config ID (site identifier) — a
  // property with only one populated is a half-configured state and
  // shouldn't show the page (e.g. Centre Point, which has neither, was
  // leaking into the nav when only the JavaScript ID was checked).
  // Mirrors the SSG-side hide + noindex logic in ssg-entry.ts so
  // navigation, sitemap, and crawler hints stay consistent. The fields
  // are custom property fields (typed as `text`) so we read them via
  // `useProperty()` directly rather than the strongly-typed
  // `usePropertyField` (which only knows the built-in scalar fields).
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;

  // Editor-configurable override. The `header.interactive_map_show_if_exists`
  // token can be bound to a property field (Field mode — e.g.
  // `custom_engrain_sightmap_url`) or given a literal string (Custom mode).
  // In both cases useContentToken returns the already-RESOLVED value (the
  // field's value, or the literal), so we simply expose the Interactive Map
  // page whenever that resolved value is non-empty. Blank by default (no
  // override). ADDITIVE to the ApartmentSync detection above, so existing
  // ApartmentSync-configured properties keep working untouched.
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveMapOverride = !!(
    interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim()
  );

  const hasInteractiveLeasing =
    (!!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
      !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim())) ||
    hasInteractiveMapOverride;

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  // Call-tracking (DNI) scripts rewrite the text inside `.dniphone` links,
  // replacing React-owned text nodes. In edit mode we drop those classes and
  // remount the links (via `key`) so React never reconciles the swapped nodes.
  const isEditMode = !!useEditMode()?.isEditMode;
  const dniClass = isEditMode ? '' : 'dniphone dniphonehref ';
  const dniKey = isEditMode ? 'edit' : 'live';

  // Content tokens
  const phone = useContentToken('header.phone');
  const callUsLabel = useContentToken('header.call_us_label');
  const officeHoursLabel = useContentToken('header.office_hours_label');

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoBlackLogo = usePropertyField('property_mono_black_logo');
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');

  // Header bar is solid white/light. Slot default is mono-black, but a
  // saved binding (or older primary default) may still resolve to the
  // mono-white asset — unreadable here. Fall back to mono-black. Compare
  // path-only so CDN query/transform params don't defeat the match. If
  // mono-black is missing, keep the resolved image rather than blanking.
  const logoUrlPath = (url?: string | null) =>
    url ? url.split('?')[0].split('#')[0] : '';
  const isLightLogo =
    !!logoRaw.image &&
    !!monoWhiteLogo &&
    logoUrlPath(logoRaw.image) === logoUrlPath(monoWhiteLogo);
  const logoImage = isLightLogo
    ? monoBlackLogo || logoRaw.image || null
    : logoRaw.image || monoBlackLogo || null;
  const logo: SlotGroupResult = { ...logoRaw, image: logoImage };

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — hook
  // builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();

  // Office hours for the mobile drawer contact block (same collection the
  // Footer renders). Grouped so Mon–Fri collapse into one line.
  const officeHours = useCollectionBinding<OfficeHour>('header.office_hours', {
    collection: 'office_hours',
  });
  const groupedHours =
    officeHours && officeHours.length > 0 ? groupOfficeHours(officeHours) : [];

  // Google Fonts
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="montserrat"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'montserrat');
    document.head.appendChild(link);
  }, []);

  // Measure header height for spacer
  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      <header
        ref={headerRef}
        className="header-scrolled fixed left-0 right-0 top-0 z-50 w-full"
        style={{ backgroundColor: 'var(--vibe-color-background)' }}
      >
        {/* Editor-only control. Holds the name of the property field whose
            presence exposes the Interactive Map page in the nav (consumed by
            `hasInteractiveMapOverride` above — e.g. `custom_engrain_sightmap_url`).
            Rendered visually hidden because it's a configuration value, not
            display copy, but wrapped in EditableToken so it's reachable from
            the editor sidebar / inline editor and satisfies the schema linter. */}
        <EditableToken
          path="header.interactive_map_show_if_exists"
          type="text"
          label="Interactive Map: Show If Exists"
          description="Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide."
          as="span"
          className="sr-only"
        >
          {interactiveMapShowIfExists || ''}
        </EditableToken>

        {/* ============================================================== */}
        {/* Single unified navigation bar                                  */}
        {/* ============================================================== */}
        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)', transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          {/* ============================================================ */}
          {/* Utility row — desktop only. Address on the left; bold phone
              sits just left of the flush Resident Access + Apply Now CTAs
              on the right. Secondary-filled bar (matches Comfy /
              Stadium House); links also live in the mobile / tablet
              drawer below. */}
          {/* ============================================================ */}
          <div className="header-util-row hidden lg:flex items-stretch h-10">
            {/* Left: address */}
            <div className="flex-1 flex items-center min-w-0 pl-4 sm:pl-6 lg:pl-8 pr-4">
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="header-util-link header-util-address"
                >
                  {addressLink.address}
                </a>
              </EditableSlotGroup>
            </div>

            {/* Right cluster: phone sits just left of the flush CTA pair */}
            <div className="flex items-stretch flex-shrink-0">
              <div className="flex items-center justify-center px-5 xl:px-6">
                <a
                  key={dniKey}
                  href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                  className={`${dniClass}header-util-link header-util-phone`}
                  aria-label="Call us"
                >
                  <EditableToken
                    path="header.phone"
                    defaultField="property_call_tracking_phone"
                    defaultValue="(555) 555-5555"
                    as="span"
                  >
                    {formatPhone(phone)}
                  </EditableToken>
                </a>
              </div>

              <div className="header-util-cta-group">
                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={residentAccessLink.url || '/resident-access'}
                    target={residentAccessLink._target || '_self'}
                    className="header-util-cta"
                  >
                    {residentAccessLink.label || 'Resident Portal'}
                  </a>
                </EditableSlotGroup>

                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '/apply-now'}
                    target={applyNowLink._target || '_self'}
                    className="header-util-cta"
                  >
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 lg:px-8">
            {/* min-w-0 + gap: on narrow viewports the logo yields space to the
                Schedule-a-Tour CTA instead of overflowing under it. Wider
                gap + logo pr keep wide wordmarks from sitting flush on the
                CTA (gap alone used to be only 8px on phone). */}
            <div className="flex items-center gap-3 sm:gap-4 h-20 lg:h-[88px] min-w-0">
              {/* Mobile / tablet hamburger — left of the logo so the mark
                  and Schedule-a-Tour CTA stay the focal points on the right. */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden relative w-10 h-10 flex items-center justify-center flex-shrink-0 -ml-1"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <span
                  className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? 'rotate-45' : '-translate-y-2'
                  }`}
                />
                <span
                  className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? 'opacity-0' : ''
                  }`}
                />
                <span
                  className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? '-rotate-45' : 'translate-y-2'
                  }`}
                />
              </button>

              {/* Logo — flex-1 + min-w-0 on <lg so wide wordmarks shrink
                  inside the remaining space between hamburger and CTA;
                  overflow-hidden clips any intrinsic image overflow that
                  would otherwise paint over the button. pr-* reserves
                  breathing room inside the flex slot even when the image
                  hits max-w-full. */}
              <EditableSlotGroup
                groupPath="header.logo"
                definition={logoSlotDef}
                className="header-logo min-w-0 flex-1 lg:flex-none overflow-hidden pr-2 sm:pr-3 lg:pr-0"
              >
                <a href="/" className="block min-w-0 max-w-full">
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logo.alt || 'Community Logo'}
                      className="h-8 sm:h-10 lg:h-12 w-auto max-w-full object-contain object-left"
                    />
                  ) : (
                    <div className="font-heading text-base sm:text-lg lg:text-xl font-normal text-[var(--vibe-color-primary)] truncate">
                      The Rise
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop Navigation Links */}
              <DesktopNav navLinks={navLinks} currentPath={currentPath} />

              {/* Right group — Schedule-a-Tour CTA. Phone / Apply Now /
                  Resident Access live in the util row (desktop) or the
                  drawer (mobile / tablet). Tighter padding / type on
                  phone so the label fits beside a constrained logo. */}
              <div className="flex items-center flex-shrink-0">
                {/* Schedule Tour CTA */}
                <EditableSlotGroup
                  groupPath="header.schedule_tour_link"
                  definition={scheduleTourLinkDef}
                  className="flex-shrink-0"
                >
                  <a
                    href={scheduleTourHref(scheduleTourLink.url || '/schedule-a-tour')}
                    data-schedule-tour-href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    onClick={handleScheduleTourClick}
                    className="nav-cta-btn group/cta relative inline-flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3 font-body text-sm sm:text-[15px] lg:text-base font-normal normal-case whitespace-nowrap overflow-hidden transition-all duration-300"
                  >
                    <span className="relative z-10">
                      {scheduleTourLink.label || 'Schedule a Tour'}
                    </span>
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Mobile / Tablet Menu Overlay                                   */}
        {/* -------------------------------------------------------------- */}
        {/* < md: Belmont-style drawer (nav → Call Us / Hours / Address →  */}
        {/* stacked .nav-cta-btn CTAs). md–lg: full-screen white overlay    */}
        {/* with close (X), centered nav rules, and a bottom util CTA row. */}
        {/* Desktop (lg+) never shows this panel.                          */}
        {/* ============================================================== */}
        <div
          className={`mobile-menu-panel lg:hidden fixed inset-x-0 bottom-0 overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{ top: `${headerHeight}px`, backgroundColor: 'var(--vibe-color-background)' }}
        >
          {/* Tablet chrome — close (X) only. Hidden on phone; the phone
              drawer keeps the header hamburger. */}
          <div className="tablet-menu-chrome hidden md:flex items-center px-6 pt-5 pb-2 flex-shrink-0">
            <button
              type="button"
              onClick={closeMobileMenu}
              className="tablet-menu-close w-11 h-11 flex items-center justify-center text-[var(--vibe-color-text)]"
              aria-label="Close menu"
            >
              <CloseIcon className="w-7 h-7" />
            </button>
          </div>

          <div className="mobile-menu-body md:flex md:flex-col md:flex-1 md:min-h-0">
            {/* Navigation links — phone: stacked under header; tablet:
                vertically centered in the remaining viewport. */}
            <nav
              role="navigation"
              aria-label="Mobile navigation"
              className="md:flex-1 md:flex md:flex-col md:justify-center md:px-10 lg:px-14"
            >
              {navLinks.map((link) => (
                <React.Fragment key={link.href}>
                  <MobileNavLink link={link} onNavigate={closeMobileMenu} currentPath={currentPath} />
                  {link.children?.map((child) => (
                    <MobileNavLink
                      key={child.href}
                      link={child}
                      onNavigate={closeMobileMenu}
                      currentPath={currentPath}
                      isChild
                    />
                  ))}
                </React.Fragment>
              ))}
            </nav>

            {/* Phone drawer contact — Call Us / Office Hours / Address. */}
            <div className="px-6 pt-8 pb-6 text-center text-[var(--vibe-color-primary)] md:hidden">
              <div className="space-y-6">
                <div className="space-y-1.5">
                  <EditableToken
                    path="header.call_us_label"
                    defaultValue="Call Us"
                    as="p"
                    className="font-body text-[11px] font-medium uppercase tracking-[0.18em] text-[var(--vibe-color-primary)]"
                  >
                    {callUsLabel || 'Call Us'}
                  </EditableToken>
                  <a
                    key={dniKey}
                    href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                    className={`${dniClass}inline-block font-body text-lg text-[var(--vibe-color-primary)] hover:opacity-80 transition-opacity`}
                  >
                    <EditableToken
                      path="header.phone"
                      defaultField="property_call_tracking_phone"
                      defaultValue="(555) 555-5555"
                      as="span"
                    >
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>
                </div>

                <div className="space-y-2">
                  <EditableToken
                    path="header.office_hours_label"
                    defaultValue="Office Hours"
                    as="p"
                    className="font-body text-[11px] font-medium uppercase tracking-[0.18em] text-[var(--vibe-color-primary)]"
                  >
                    {officeHoursLabel || 'Office Hours'}
                  </EditableToken>
                  {groupedHours.length > 0 ? (
                    <div className="font-body text-sm leading-relaxed text-[var(--vibe-color-primary)] space-y-0.5">
                      {groupedHours.map((group, index) => (
                        <p key={index}>
                          {formatDrawerHoursLabel(group)}: {group.hours}
                        </p>
                      ))}
                    </div>
                  ) : null}
                </div>

                <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                  <a
                    href={addressLink.url || propertyAddressUrl || '#'}
                    target={addressLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="inline-block font-body text-sm text-[var(--vibe-color-primary)] hover:opacity-80 transition-opacity"
                  >
                    {addressLink.address}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>

            {/* Phone drawer CTAs — full-width stack matching header
                schedule_tour_link (`.nav-cta-btn`). */}
            <div className="px-6 pb-8 pt-2 space-y-3 border-t border-[var(--vibe-color-border)] md:hidden">
              <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                <a
                  href={scheduleTourHref(scheduleTourLink.url || '/schedule-a-tour')}
                  data-schedule-tour-href={scheduleTourLink.url || '/schedule-a-tour'}
                  target={scheduleTourLink._target || '_self'}
                  onClick={(e) => {
                    handleScheduleTourClick(e);
                    closeMobileMenu();
                  }}
                  className="nav-cta-btn group/cta relative flex w-full items-center justify-center gap-2 px-4 py-3 font-body text-base font-normal normal-case whitespace-nowrap overflow-hidden transition-all duration-300"
                >
                  <span className="relative z-10">
                    {scheduleTourLink.label || 'Schedule a Tour'}
                  </span>
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className="nav-cta-btn group/cta relative flex w-full items-center justify-center gap-2 px-4 py-3 font-body text-base font-normal normal-case whitespace-nowrap overflow-hidden transition-all duration-300"
                >
                  <span className="relative z-10">
                    {applyNowLink.label || 'Apply Now'}
                  </span>
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className="nav-cta-btn group/cta relative flex w-full items-center justify-center gap-2 px-4 py-3 font-body text-base font-normal normal-case whitespace-nowrap overflow-hidden transition-all duration-300"
                >
                  <span className="relative z-10">
                    {residentAccessLink.label || 'Resident Portal'}
                  </span>
                </a>
              </EditableSlotGroup>
            </div>

            {/* Tablet bottom CTAs — Resident Access | Apply Now | Schedule
                a Tour as a single filled primary row (matches mock). */}
            <div className="tablet-menu-cta-row hidden md:grid grid-cols-3 gap-3 px-8 pb-8 pt-6 flex-shrink-0">
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className="tablet-menu-cta block text-center py-3.5 px-3 font-body text-sm font-medium tracking-wide"
                >
                  {residentAccessLink.label || 'Resident Portal'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className="tablet-menu-cta block text-center py-3.5 px-3 font-body text-sm font-medium tracking-wide"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                <a
                  href={scheduleTourHref(scheduleTourLink.url || '/schedule-a-tour')}
                  data-schedule-tour-href={scheduleTourLink.url || '/schedule-a-tour'}
                  target={scheduleTourLink._target || '_self'}
                  onClick={(e) => {
                    handleScheduleTourClick(e);
                    closeMobileMenu();
                  }}
                  className="tablet-menu-cta block text-center py-3.5 px-3 font-body text-sm font-medium tracking-wide"
                >
                  {scheduleTourLink.label || 'Schedule a Tour'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </header>

      {/* Flow spacer. The header is fixed + opaque, so it would otherwise sit
          on top of the page content. This reserves its height in the document
          flow so the hero (and every page below it) starts beneath the bar.
          Height tracks the measured header height once JS runs; the CSS
          fallback in the <style> block (offer-banner var + nav/util row
          heights) keeps the gap correct on first paint / SSG before the
          measurement, minimizing layout shift. */}
      <div
        className="header-spacer"
        aria-hidden="true"
        style={headerHeight ? { height: `${headerHeight}px` } : undefined}
      />

      <style>{`
        /* Spacer fallback height before JS measures the real header. Mobile
           / tablet show only the nav row (h-20 = 5rem); desktop (lg) adds
           the utility row (h-10 = 2.5rem) above the taller nav row (88px =
           5.5rem). The offer-banner var is added on top because the banner
           is fixed and the header reserves its height via header-bar padding. */
        .header-spacer {
          height: calc(var(--offer-banner-height, 0px) + 5rem);
        }
        @media (min-width: 1024px) {
          .header-spacer {
            height: calc(var(--offer-banner-height, 0px) + 8rem);
          }
        }

        /* ================================================================ */
        /* Single navigation bar                                           */
        /* ================================================================ */
        .header-bar {
          /* Match the background-color transition curve so border + shadow
             fade in/out in lock-step with the transparent → solid swap. */
          border-bottom: 1px solid transparent;
          transition:
            border-color 0.5s ease,
            box-shadow 0.5s ease;
        }
        .header-scrolled .header-bar {
          border-bottom-color: var(--vibe-color-border);
          box-shadow:
            0 1px 3px rgba(0,0,0,0.04),
            0 4px 12px rgba(0,0,0,0.03);
        }
        .header-transparent .header-bar {
          border-bottom-color: transparent;
          box-shadow: none;
        }

        /* Transparent header: nav links → white (header bar only — NOT inside the mobile menu panel) */
        .header-transparent .header-bar a.font-body,
        .header-transparent .header-bar nav a,
        .header-transparent .header-bar button.font-body {
          color: rgba(255,255,255,0.92) !important;
        }
        .header-transparent .header-bar nav a:hover,
        .header-transparent .header-bar button.font-body:hover {
          color: #fff !important;
        }

        /* Interior pages (transparent nav over HeroSolidBG / primary color):
           swap white for the luminance-picked on-color token (textOnPrimary
           vs textOnSecondary, whichever has higher WCAG contrast against the
           primary background). The variable is set inline on the header. */
        .header-transparent.header-over-solid-hero .header-bar a.font-body,
        .header-transparent.header-over-solid-hero .header-bar nav a,
        .header-transparent.header-over-solid-hero .header-bar button.font-body {
          color: var(--rise-nav-over-hero-text, rgba(255,255,255,0.92)) !important;
        }
        .header-transparent.header-over-solid-hero .header-bar nav a:hover,
        .header-transparent.header-over-solid-hero .header-bar button.font-body:hover {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }

        /* "More" dropdown panel sits on a theme-background (white) surface,
           so its links use theme text color regardless of the header bar's
           transparent state above. The dropdown is a white-background
           context, so the hover affordance is the animated underline from
           .rise-nav-darken-hover below — we keep the text color steady
           on hover (still !important to override the parent
           .header-transparent .header-bar nav a:hover white rule).

           The .header-over-solid-hero variants must be listed explicitly:
           .header-transparent.header-over-solid-hero .header-bar nav a has
           higher specificity (0,4,1) than the plain .header-transparent
           .header-bar .nav-more-dropdown a override (0,3,1) and would
           otherwise force the dropdown links to the picked on-hero color
           (white) — producing white-on-white text inside the dropdown on
           interior pages like Floor Plans. */
        .header-bar .nav-more-dropdown a,
        .header-transparent .header-bar .nav-more-dropdown a,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a {
          color: var(--vibe-color-text) !important;
        }
        .header-bar .nav-more-dropdown a:hover,
        .header-transparent .header-bar .nav-more-dropdown a:hover,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a:hover {
          color: var(--vibe-color-text) !important;
        }

        /* Mobile menu panel uses --vibe-color-background (white), so links
           must render in theme text/primary color regardless of the header
           bar's transparent / scrolled state above it. Nav rows keep a
           steady color on hover; CTAs use .nav-cta-btn (white label over
           primary / surface fills — see rules below). */
        .mobile-menu-panel a,
        .mobile-menu-panel button {
          color: var(--vibe-color-primary);
        }
        .mobile-menu-panel a:hover {
          color: var(--vibe-color-primary);
        }
        /* Keep drawer CTAs on the same white-on-primary / white-on-surface
           treatment as the header schedule_tour_link (.nav-cta-btn). */
        .mobile-menu-panel a.nav-cta-btn,
        .mobile-menu-panel a.nav-cta-btn:hover {
          color: #ffffff;
        }

        /* ---------------------------------------------------------------- */
        /* Mobile drawer nav rows — hairline separators only (Belmont).    */
        /* ----------------------------------------------------------------- */
        /* Override the desktop "double bar" (.rise-nav-darken-hover /
           .nav-link-active) so the vertical drawer stays a clean list with
           thin borders. Active page gets a slightly stronger weight; hover
           nudges opacity. Scoped under .mobile-menu-panel so DESKTOP IS
           UNTOUCHED. */
        .mobile-menu-panel .rise-nav-darken-hover {
          box-shadow: none;
          background-color: transparent;
          transition: opacity 0.2s ease, color 0.2s ease;
        }
        .mobile-menu-panel .rise-nav-darken-hover:hover {
          box-shadow: none;
          background-color: transparent;
          opacity: 0.7;
          color: var(--vibe-color-primary);
        }
        .mobile-menu-panel .nav-link-active {
          box-shadow: none;
          background-color: transparent;
          font-weight: 500;
          color: var(--vibe-color-primary);
        }

        /* Nav links: the hover affordance is the SAME "double bar" (top +
           bottom) used for the active page — this replaces the old single
           underline. Bars are drawn with inset box-shadows so they add no
           layout size and never shift neighbors; the transparent base lets
           them fade in smoothly on hover. Used by the Header desktop links,
           the "More" dropdown items, and the mobile drawer rows. */
        .rise-nav-darken-hover {
          box-shadow:
            inset 0 2px 0 0 transparent,
            inset 0 -2px 0 0 transparent;
          transition: box-shadow 0.2s ease, color 0.2s ease;
        }
        .rise-nav-darken-hover:hover {
          box-shadow:
            inset 0 2px 0 0 var(--vibe-color-text),
            inset 0 -2px 0 0 var(--vibe-color-text);
        }

        /* Active-page indicator — a "double bar" above AND below the current
           page's nav label. Drawn with inset box-shadows (top + bottom) so it
           adds no width/height and never shifts neighboring links between the
           active and inactive states. The bars span the link's content box:
           the text width for the inline top-level links, and the full row for
           the dropdown / mobile rows. Uses the theme text color so it reads as
           a crisp dark rule on the white header bar. */
        .nav-link-active {
          box-shadow:
            inset 0 2px 0 0 var(--vibe-color-text),
            inset 0 -2px 0 0 var(--vibe-color-text);
        }

        /* Dot separator between desktop nav page names. The margin-right
           matches the (now smaller) nav flex gap (gap-2 → gap-3 at xl) so the
           gap before the dot and the margin after it are equal — the dot sits
           centered between the two links, and the total link-to-link spacing
           is back to roughly what it was before separators were added (rather
           than the doubled spacing that stacked a full gap on each side).
           Full text color + heavier weight + a slightly larger glyph make the
           dot crisp and clearly defined instead of a faint speck. */
        .nav-sep {
          color: var(--vibe-color-text);
          font-weight: 400;
          font-size: 1.15em;
          margin-right: 0.5rem;
          line-height: 1;
          user-select: none;
        }
        @media (min-width: 1280px) {
          .nav-sep { margin-right: 0.75rem; }
        }


        /* ================================================================ */
        /* Tablet menu overlay (md–lg) — matches the full-screen mock.     */
        /* ----------------------------------------------------------------- */
        /* Phone (< md) keeps the under-header drawer. Desktop (lg+) never
           mounts this panel. On tablet the panel covers the viewport from
           the top (overrides the inline headerHeight top), lays out as a
           column (chrome / centered nav / bottom CTAs), and swaps the
           phone drawer chrome for the close control + three filled util
           buttons. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .mobile-menu-panel {
            top: 0 !important;
            display: flex;
            flex-direction: column;
            overflow-y: auto;
          }

          /* EditableSlotGroup wrappers must not break the 3-col CTA grid. */
          .tablet-menu-cta-row > * {
            display: contents;
          }

          .mobile-menu-panel a.tablet-menu-cta,
          .mobile-menu-panel a.tablet-menu-cta:hover {
            background-color: var(--vibe-color-primary);
            color: var(--vibe-color-text-on-primary);
            border-radius: 5px;
            transition: background-color 0.2s ease, color 0.2s ease;
          }
          .mobile-menu-panel a.tablet-menu-cta:hover {
            background-color: var(--vibe-color-surface);
            color: #ffffff;
          }

          /* Nav rows: larger touch targets, full-width hairline rules, no
             left accent bar (the mock is a clean rule list). Top border on
             the first link frames the stack. */
          .mobile-menu-panel .rise-nav-darken-hover {
            padding: 1.35rem 0.5rem;
            font-size: 1.125rem;
            letter-spacing: 0.04em;
            border-bottom-color: var(--vibe-color-border);
            box-shadow: none;
            background-color: transparent;
          }
          .mobile-menu-panel nav > a.rise-nav-darken-hover:first-of-type {
            border-top: 1px solid var(--vibe-color-border);
          }
          .mobile-menu-panel .rise-nav-darken-hover:hover {
            padding-left: 0.5rem;
            box-shadow: none;
            background-color: transparent;
            color: var(--vibe-color-primary);
          }
          .mobile-menu-panel .nav-link-active {
            padding-left: 0.5rem;
            box-shadow: none;
            background-color: transparent;
            color: var(--vibe-color-primary);
          }
        }

        /* Transparent header: hamburger bars → white */
        .header-transparent span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: #fff !important;
        }

        /* Transparent header: site name fallback text */
        .header-transparent .font-heading {
          color: #fff !important;
        }

        /* Interior pages: hamburger bars + site-name fallback also flip to
           the luminance-picked on-color token. */
        .header-transparent.header-over-solid-hero span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: var(--rise-nav-over-hero-text, #fff) !important;
        }
        .header-transparent.header-over-solid-hero .font-heading {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }

        /* ================================================================ */
        /* Utility row — sits above the main nav row inside .header-bar.   */
        /* ----------------------------------------------------------------- */
        /* Secondary-colored top bar (Aion gray, matching Comfy / Stadium
           House): address | phone | util links. Text uses text-on-secondary
           so contrast stays WCAG-safe when the brand secondary swaps. The
           Special Offer banner above uses primary (blue). */
        .header-util-row {
          background-color: var(--vibe-color-secondary);
          border-bottom: none;
        }

        /* ================================================================ */
        /* Inline utility links (address, phone, apply, resident)          */
        /* ================================================================ */
        .header-util-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.15rem 0;
          font-family: var(--vibe-font-body);
          font-size: 13px;
          font-weight: 400;
          letter-spacing: 0.01em;
          text-transform: none;
          color: var(--vibe-color-text-on-secondary);
          white-space: nowrap;
          transition: opacity 0.2s ease;
        }
        .header-util-link:hover {
          opacity: 0.85;
          color: var(--vibe-color-text-on-secondary);
        }
        /* Address can get long — truncate rather than shove the phone off-center. */
        .header-util-address {
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }
        /* Center phone reads as the util-row focal point. */
        .header-util-phone {
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.02em;
        }
        .header-util-phone:hover {
          opacity: 1;
        }

        /* Flush util CTAs (Resident Access | Apply Now) — seamless pair
           pinned to the right edge. Default blends with the secondary bar;
           hover lifts to a lighter mix of secondary + white. */
        .header-util-cta-group {
          display: flex;
          align-items: stretch;
          flex-shrink: 0;
          align-self: stretch;
        }
        /* EditableSlotGroup wrappers should not interrupt the flex row. */
        .header-util-cta-group > * {
          display: contents;
        }
        .header-util-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          /* Tighter on tablet so address + phone + both CTAs fit; open
             back up at lg where the util row has more horizontal room. */
          padding: 0 0.85rem;
          font-family: var(--vibe-font-body);
          font-size: 13px;
          font-weight: 500;
          letter-spacing: 0.01em;
          text-transform: none;
          white-space: nowrap;
          color: var(--vibe-color-text-on-secondary);
          background-color: transparent;
          transition: background-color 0.2s ease, color 0.2s ease;
        }
        @media (min-width: 1024px) {
          .header-util-cta {
            padding: 0 1.75rem;
          }
        }
        .header-util-cta:hover {
          background-color: color-mix(in srgb, var(--vibe-color-secondary) 55%, white);
          color: var(--vibe-color-text-on-secondary);
        }

        /* ================================================================ */
        /* CTA Button — solid filled style (Aion Management)               */
        /* ----------------------------------------------------------------- */
        /* Aion Management override of the Belmont CTA: solid PRIMARY fill,
           no border, 5px corner radius, and the hover state swaps the fill
           to SURFACE -- replacing Belmont's secondary-fill + ::before darken
           combo. Label colors are picked to keep contrast strong on the
           Aion palette where surface (property_color_3) skews DARK -- the
           previous theme-text hover color produced dark-on-dark. Both the
           base primary fill and the surface hover fill are dark here, so
           the label stays WHITE across both states. When picking font
           colors on this theme going forward, mirror this: check the
           actual token values, not the token names -- surface isn't always
           a light neutral on Aion properties. The ::before overlay from
           the parent is inertized (transparent) so it can't stack on top
           of the surface hover fill. */
        .nav-cta-btn {
          background: var(--vibe-color-primary);
          border: 0;
          border-radius: 5px;
          color: #ffffff;
          box-shadow: none;
          transition: background-color 0.25s ease, color 0.25s ease,
            box-shadow 0.25s ease, transform 0.25s ease;
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: transparent;
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .nav-cta-btn:hover {
          background: var(--vibe-color-surface);
          color: #ffffff;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
          transform: translateY(-1px);
        }
        .nav-cta-btn:hover::before {
          opacity: 0;
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        /* Over a transparent header (photographic or solid hero): keep the
           solid primary fill; no border to soften. Label stays white in
           both states since both fills (primary base + surface hover) are
           dark on the Aion palette. */
        .header-transparent .nav-cta-btn {
          border: 0;
          color: #ffffff;
        }
        .header-transparent .nav-cta-btn:hover {
          border: 0;
          color: #ffffff;
        }

      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.call_us_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'header.office_hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
    'header.interactive_map_show_if_exists': {
      label: 'Interactive Map: Show If Exists',
      type: 'text',
      description:
        'Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide.',
      defaultValue: '',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_black_logo', suggestedFields: ['property_mono_black_logo', 'property_primary_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Portal' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule a Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        // URL has no defaultField — defaults to usePropertyAddressUrl() at
        // render time. Editor overrides still win.
        url: {
          label: 'URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
